/* eslint-disable react-hooks/exhaustive-deps */
import trim from "lodash/trim"
import round from "lodash/round"
import now from "lodash/now"
import isUndefined from "lodash/isUndefined"
import isEmpty from "lodash/isEmpty"
import invert from "lodash/invert"
import get from "lodash/get"
import forEach from "lodash/forEach"
import debounce from "lodash/debounce"
import { useState, useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import aa from "search-insights"
import { makeStyles } from "tss-react/mui"
import { getWindow } from "utils-lib/getWindow"
import { getTimeLeftSec } from "utils-lib/time"
import {
  resetDataLayer,
  updateDataLayer,
  updateVisitorDL,
  getDLVarFromParam,
  updatePageDL,
} from "utils-lib/analytics/dataLayer"
import { isUSCanadaAccount } from "utils-lib/isUSCanadaAccount"
import { authClient } from "constants-lib/okta"
import { localStorageKey } from "constants-lib/localStorage"
import {
  userType,
  TIME_BETWEEN_CHECKING_ACTIVITY,
  TIME_BETWEEN_SESSION_STATUS,
  IDLE_TIME_ALLOWED_BEFORE_AUTO_REFRESH,
  TIME_LEFT_BEFORE_SHOWING_EXPIRATION_WARNING_SEC,
  TIME_LEFT_BEFORE_AUTO_LOGOUT_SEC,
} from "constants-lib/authentication"
import { lob as LOB, API_STATUSES } from "constants-lib/common"
import { LanguageContext } from "contexts-lib/LanguageContext"
import { AuthenticationContext } from "contexts-lib/AuthenticationContext"
import { AEMPageContentContext } from "contexts-lib/AEMPageContentContext"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import { useLDClient } from "launchdarkly-react-client-sdk"
import { usePrevious } from "hooks-lib/usePrevious"
import injectSEOSchema from "utils-lib/seo/injectSEOSchema"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { getLanguageFromRoute } from "utils-lib/route"
import {
  updateWebsiteJSONLD,
  updateOrganizationJSONLD,
} from "utils-lib/seo/homepage"
import { addShowOrHideChatbotOnPage } from "utils-lib/chat"
import {
  updateGlobalPageLoad,
  updateVisitorObject,
} from "utils-lib/analytics/adobeDataLayer"
import { persistor } from "@store/store"

const LogOutModal = dynamic(
  import("component-lib/Base/components/LogOutModal/LogOutModal"),
  { ssr: false },
)
const SessionExpirationModal = dynamic(
  import(
    "component-lib/Base/components/SessionExpirationModal/SessionExpirationModal"
  ),
  { ssr: false },
)

const window = getWindow()
window.adobeDataLayer = window.adobeDataLayer || []

const useStyles = makeStyles()(() => ({
  children: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    overflowX: "hidden",
  },
}))

export const AppWrapper = ({
  getToken,
  getLinkedAccounts,
  getAllCustomer,
  getUserPaymentDetails,
  refreshToken,
  resetUserAccount,
  revokeToken,
  setLastActivityTime,
  setSessionIntervalId,
  setToken,
  getUserDetails,
  setUserDetails,
  setUserState,
  resetCartDetails,
  authentication,
  customerSelections,
  getUserAccountBalance,
  siteLanguage,
  userAccount,
  userManageAccount,
  isBrokerAccount,
  myPaymentCheckout,
  setSelectedCurrency,
  children,
  model,
  switchLanguage,
  getGlobalAlerts,
  aemPageData,
  getAEMPageJSON,
}) => {
  const [closeExpirationModal, setCloseExpirationModal] = useState(false)
  const [showExpirationWarning, setShowExpirationWarning] = useState(false)
  const [showLogOutModal, setShowLogOutModal] = useState(false)
  const [isAuthInitialized, setIsAuthInitialized] = useState(false)
  const [isLanguageInitialized, setIsLanguageInitialized] = useState(false)

  const prevUserState = usePrevious(userAccount?.userState)
  const prevLocationPathname = usePrevious(window.location.pathname)
  const [locationPathnameChanged, setLocationPathnameChanged] = useState(false)

  const { LDFFClearChatbotReduxOnLogout, LDFFReleaseSetConsistentLRSessionId } =
    useFeatureFlags([
      "LDFFClearChatbotReduxOnLogout",
      "LDFFReleaseSetConsistentLRSessionId",
    ])

  const ldClient = useLDClient()
  const [isLDClientReady, setIsLDClientReady] = useState(false)

  const { classes } = useStyles()
  const pushedACDL = useRef(false)

  const debounceSetLastActivityTime = debounce(
    () => {
      // this method gets annoying during development so turning it off
      // on local environment only
      setLastActivityTime()
    },
    TIME_BETWEEN_CHECKING_ACTIVITY,
    {
      maxWait: TIME_BETWEEN_CHECKING_ACTIVITY,
    },
  )

  /* This code gets called the first time the app (website) is loaded (refreshed). */
  useEffect(() => {
    handleInit()

    return handleCleanup
  }, [])

  useEffect(() => {
    // moved this from the header to fix redux hydration issues
    isAuthInitialized && getGlobalAlerts()
  }, [isAuthInitialized])

  useEffect(() => {
    if (ldClient?.waitForInitialization) {
      ldClient
        .waitForInitialization(5)
        .then(() => setIsLDClientReady(true))
        .catch(() => setIsLDClientReady(true))
    } else {
      setIsLDClientReady(true)
    }
  }, [ldClient])

  useEffect(() => {
    if (LDFFReleaseSetConsistentLRSessionId) {
      updateVisitorObject({
        userAccount,
        userManageAccount,
        LDFFReleaseSetConsistentLRSessionId,
      })
    }
  }, [LDFFReleaseSetConsistentLRSessionId])

  useEffect(() => {
    const satellite = window._satellite
    if (!isUndefined(satellite)) {
      satellite.track(`pageview_cms`, window._dl)

      adobeTargetCall(window.location)
    }
  }, [window._satellite])

  useEffect(() => {
    const { userState } = userAccount

    if (userState) {
      if (
        userState ===
        get(window, `localStorage.${localStorageKey.USER_STATE}`, "")
      ) {
        updateVisitorDL({
          userAccount,
          userManageAccount,
        })
        updateVisitorObject({
          userAccount,
          userManageAccount,
          LDFFReleaseSetConsistentLRSessionId,
        })
      }

      const satellite = window._satellite
      if (!isUndefined(satellite)) {
        /** Triggering Adobe target call only for below user states */
        if (
          userState !== prevUserState &&
          (userState === userType.ANONYMOUS ||
            userState === userType.PROSPECTIVE ||
            userState === userType.COOKIED)
        ) {
          adobeTargetCall(window.location)
        }
      }

      const shouldFetchAccountData =
        userManageAccount?.getLinkedAccountStatus !== API_STATUSES.PENDING &&
        userManageAccount?.getLinkedAccountStatus !== API_STATUSES.FULFILLED

      // behavior is different for pref center and manage contacts login, so make sure we are able to fetch linked accounts without a page refresh
      if (userState === userType.LOGGED_IN && shouldFetchAccountData) {
        fetchAccountData()
      }
    }
  }, [userAccount?.userState])

  useEffect(() => {
    if (window.location.pathname !== prevLocationPathname) {
      setLocationPathnameChanged(true)
    }
    return undefined
  }, [window.location.pathname])

  useEffect(() => {
    if (locationPathnameChanged) {
      const { templateName = "", lobType = "", pageType = "" } = model

      // updated to use get to avoid errors if localisationProperties is undefined, this field was removed on the AEM side and caused errors due to unsafe access
      const gisIds = get(model, "localisationProperties.gisIds", "")

      if (!pushedACDL.current && isLDClientReady) {
        updateGlobalPageLoad({
          previous_page_name:
            getDLVarFromParam(`previous-pagename`) ||
            get(window, `_dl.page.pagename`, ``) ||
            get(window, `localStorage.${localStorageKey?.DL_PAGE_NAME}`, ``),
          language: siteLanguage?.language,
          userAccount,
          userManageAccount,
          templateName: templateName || ``,
          lobType: lobType || ``,
          gisIDs: gisIds || ``,
          pageType: pageType || ``,
        })
        pushedACDL.current = true
        changeRefState()
      }
      adobeTargetCall(window.location)

      /* make sure to add/hide chatbot when location changes */
      addShowOrHideChatbotOnPage(model, false, true)

      updateWebsiteJSONLD()
      updateOrganizationJSONLD()
    }
  }, [locationPathnameChanged, isLDClientReady])

  useEffect(() => {
    /*
      Since page reload doesn't occur on LOB switch, in order to log home page analytics page view information,
      fire an event when customerSelections.customerLob changes.
    */
    const previousPageName =
      getDLVarFromParam(`previous-pagename`) ||
      get(window, `_dl.page.pagename`, ``) ||
      get(window, `localStorage.${localStorageKey.DL_PAGE_NAME}`, ``)

    /* Log variables into data layer */
    updatePageDL({
      previous_page_name: previousPageName,
      language: siteLanguage.language,
    })
  }, [customerSelections.customerLob])

  useEffect(() => {
    // handle async issue with SSR redux hydration
    if (!isLanguageInitialized) {
      setIsLanguageInitialized(true)
    } else {
      updateAppLocaleToUrlLocale()
    }
  }, [siteLanguage.language, isLanguageInitialized])

  async function handleInit() {
    addShowOrHideChatbotOnPage(model, false, true)

    const urls = await import("utils-lib/builds").then((mod) => mod.urls)

    injectSEOSchema()

    aa("init", {
      appId: urls.apiKey.GUEST.ALGOLIA_APP_ID,
      apiKey: urls.apiKey.GUEST.ALGOLIA_API_KEY,
    })

    /* Check if the user is logged in. */
    const { userState } = userAccount

    if (userState === userType.LOGGED_IN || userState === userType.LOGGING_IN) {
      /* https://github.com/okta/okta-auth-js#sessionget */
      authClient.session
        .get()
        .then((session) => {
          console.warn(
            `session.get() fulfilled with session response:`,
            session,
          )

          if (session && get(session, `status`) !== `INACTIVE`) {
            console.warn(
              `App > componentDidMount > authClient.session.get() > User is logged in`,
            )

            /* The user is logged in. */
            getToken().then((accessToken) => {
              onLoginSuccess(accessToken)
            })
          } else {
            console.warn(
              `App > componentDidMount > authClient.session.get() > User is NOT logged in`,
            )
            onLoginFail()
          }
        })
        .catch((error) => {
          console.warn(`session.get() failed with error:`, error)

          onLoginFail()
        })
    } else {
      /* If user is in any logged out state, check if there's an active session that may have started
           from another app (e.g. CloudCraze). */
      checkIfLoggedInElsewhere()
    }

    window._dl = window._dl || resetDataLayer()

    const { templateName, lobType, primaryGisId } = model

    // updated to use get to avoid errors if localisationProperties is undefined, this field was removed on the AEM side and caused errors due to unsafe access
    const gisIds = get(model, "localisationProperties.gisIds", "")

    /* Log variables into data layer */
    updateDataLayer({
      previous_page_name:
        getDLVarFromParam(`previous-pagename`) ||
        get(window, `_dl.page.pagename`, ``) ||
        get(window, `localStorage.${localStorageKey.DL_PAGE_NAME}`, ``),
      templateName: templateName || ``,
      lobType: lobType || ``,
      gisIDs: gisIds || ``,
      primaryGisId: primaryGisId || ``,
      language: siteLanguage.language,
      userAccount,
      userManageAccount,
      clicks: {
        ui_element:
          decodeURI(getDLVarFromParam(`ui_element`) || ``) ||
          get(window, `_dl.clicks.ui_element`, ``) ||
          get(window, `localStorage.${localStorageKey.DL_UI_ELEMENT}`, ``),
        object_content:
          decodeURI(getDLVarFromParam(`object_content`) || ``) ||
          get(window, `_dl.clicks.object_content`, ``) ||
          get(window, `localStorage.${localStorageKey.DL_OBJECT_CONTENT}`, ``),
      },
    })
  }

  function handleCleanup() {
    debounceSetLastActivityTime.cancel()
    stopTrackingAuthenticationStatus()
  }

  /*
    If the url's locale does not match the saved language in the app, update app language to match
    the one in the url.
  */
  function updateAppLocaleToUrlLocale() {
    const appLanguage = siteLanguage.language
    const urlLanguage = getLanguageFromRoute()

    if (window.location.pathname === "/") {
      const modelLanguage = getLanguageFromRoute(model.cqPath)

      // If the app language on the / route doesn't match the model language, redirect to the proper language route (for homepage / route only).
      if (appLanguage !== modelLanguage) {
        if (!siteLanguage.hasUserUpdatedLanguage) {
          switchLanguage(modelLanguage, true)
        } else {
          const languageRoute = getLanguageRoute(appLanguage)
          // trigger a hard navigtion to prevent cached / page content from displaying, handles the edge case where the user deletes the path from the URL and navigates that way
          window.location.href = `${window.location.origin}${languageRoute}`
        }
      }
    } else if (!isEmpty(urlLanguage) && urlLanguage !== appLanguage) {
      switchLanguage(urlLanguage, true)
    }
  }

  function onLoginFail() {
    if (userAccount.userState === userType.LOGGING_IN) {
      /* User should have logged in but didn't. */
      console.warn(`User login failed.`)
    } else {
      /* The user is NOT logged in. */
      // eslint-disable-next-line no-lonely-if
      if (hasAddress) {
        resetUserAccount(userType.COOKIED)
        resetCartDetails()
      } else {
        resetUserAccount(userType.ANONYMOUS)
      }
    }

    setIsAuthInitialized(true)
  }

  function startTrackingAuthenticationStatus() {
    const sessionIntervalId = setInterval(
      checkAuthenticationStatus,
      TIME_BETWEEN_SESSION_STATUS,
    )
    setSessionIntervalId(sessionIntervalId)
  }

  function stopTrackingAuthenticationStatus() {
    const sessionIntervalId = get(userAccount, `sessionIntervalId`, null)

    if (sessionIntervalId !== null) {
      clearInterval(sessionIntervalId)
      setSessionIntervalId(null)
    }
  }

  function checkIfLoggedInElsewhere() {
    console.warn(`checkIfLoggedInElsewhere`)
    /* Try to get an active session. Could be active from another app such as CloudCraze. */
    authClient.session.exists().then((exists) => {
      /* Check if an active session exists before calling the Okta auth session.get().  */
      console.warn(`checkIfLoggedInElsewhere exists?`, exists)
      if (exists) {
        console.warn(`checkIfLoggedInElsewhere about to call session.get()`)
        authClient.session.get().then((session) => {
          console.warn(
            `checkIfLoggedInElsewhere called session.get() and got session:`,
            session,
          )
          console.warn(
            `Waiting for Okta's authClient.token.getWithoutPrompt() to return...`,
          )
          /* Make sure user is logged in. */
          authClient.token
            .getWithoutPrompt({
              responseType: `token`,
            })
            .then((token) => {
              // Okta token object has changed the structure, but to be safe we are checking the old structure as well in case this changes again
              const accessToken = !isEmpty(token?.accessToken)
                ? token
                : token?.tokens?.accessToken

              console.warn(
                `Okta's authClient.token.getWithoutPrompt() returned with the access token:`,
                accessToken,
              )

              /* User is logged in from an active session from another app). */
              setToken(accessToken)

              onLoginSuccess(accessToken)

              /* Save user details. */
              const userId = get(session, `userId`, ``)
              const login = get(session, `login`, ``)
              const data = {
                userId,
                login,
              }
              setUserDetails(data)

              /* Get user details - first name and last name do not come with the session object. */
              getUserDetails({
                token: accessToken,
                userId,
              })
            })
            .catch((e) => {
              console.warn(
                `Okta's authClient.token.getWithoutPrompt() failed with error:`,
                e,
              )
              checkLoggedOutState()
            })
        })
      } else {
        /* The user is NOT logged in. */
        checkLoggedOutState()
      }
    })
  }

  /* Check if an address is saved in redux. */
  function hasAddress() {
    return (
      get(customerSelections, `customerAddress.formattedAddress`, ``) !== ``
    )
  }

  /*
    Determine which logged out state we're in.
    This method should only be called once we've checked okta session to
    determine that we're not logged in (from any app).
  */
  function checkLoggedOutState() {
    /*
      If user is cookied, don't change the state, because the only way we know
      if a user is cookied is if they're already set as cookied in userAccount.userState.

      A user is cookied if:
      1. An address is saved in redux.
      2. User state has been marked as userType.COOKIED.
         userAccount.userState is set to userType.COOKIED on logout,
         so if we see that the current userAccount.userState is userType.COOKIED
         then we know the user was logged in previously.

      Check if user is PROSPECTIVE.
      A user is a prospective user if:
      1. An address is saved in redux.
      2. Current user state in redux is not userType.COOKIED.
    */
    if (hasAddress()) {
      resetUserAccount(userType.PROSPECTIVE, false)
      resetCartDetails()
    } else {
      resetUserAccount(userType.ANONYMOUS)
    }

    setIsAuthInitialized(true)
  }

  /* Get user account details and user payment information. */
  const fetchUserPaymentDetails = async (linkedAccounts) => {
    const isMultiCountry = isUSCanadaAccount(userManageAccount)
    const selectedCurrency = get(myPaymentCheckout, `selectedCurrency`, `USD`)
    if (linkedAccounts.length > 0) {
      if (isMultiCountry && selectedCurrency !== `USD`) {
        setSelectedCurrency(`USD`)
      }
      getUserPaymentDetails()
    }
  }

  const fetchAccountData = async () => {
    const { linkedAccounts } = await getLinkedAccounts()
    if (isBrokerAccount) {
      getUserAccountBalance()
    } else {
      await getAllCustomer()
      fetchUserPaymentDetails(linkedAccounts)
    }
  }

  /* This is called after logging in either from this app or from another. */
  async function onLoginSuccess(token) {
    if (token !== null) {
      const logRocketIdentify = await import("utils-lib/logRocket").then(
        (mod) => mod.logRocketIdentify,
      )

      const { firstName, lastName, login } = userAccount.userDetails

      logRocketIdentify({
        name: `${firstName} ${lastName}`,
        email: login,
      })

      /* If we were able to log in by using an access token generated from
         another app such as CloudCraze or old canada-app, then we need to
         verify that the redux state matches the new user state. */
      if (userAccount.userState !== userType.LOGGED_IN) {
        /* Update the user redux state (from LOGGING_IN to LOGGED_IN
           if we logged in from this app). */
        setUserState(userType.LOGGED_IN)
      }

      /*
        Now that we're logged in, get the following details about the user:
        - Linked accounts
        - Payment details
        - User address

        If customerSelections address is empty, then set default address
        - Redirected from WMX (already logged in) -> do not overwrite selected address and associated info

        Then set default address information in customerSelections.
        Single account users: the address for that one account
        Multi account users: default to the first address in userManageAccount.linkedAccounts
        Non-logged in users: the last searched address if any exists (customerSelections.customerAddress).
      */

      fetchAccountData()

      /* update visitor data layer */
      updateVisitorDL({
        userAccount,
        userManageAccount,
      })
      updateVisitorObject({
        userAccount,
        userManageAccount,
        LDFFReleaseSetConsistentLRSessionId,
      })

      /* Start tracking user activity to see how long they're idle for. */
      debounceSetLastActivityTime()
      document.onclick = debounceSetLastActivityTime
      document.onmousemove = debounceSetLastActivityTime
      document.onkeypress = debounceSetLastActivityTime

      /* Start tracking user's authentication status. */
      startTrackingAuthenticationStatus()

      setIsAuthInitialized(true)
    }
  }

  function checkAuthenticationStatus() {
    const expiresAt = get(userAccount, `token.expiresAt`, null)

    if (userAccount.userState === userType.LOGGED_IN && expiresAt !== null) {
      const accessTokenTimeLeftSec = getTimeLeftSec(expiresAt)

      console.warn(
        `${round(accessTokenTimeLeftSec)} seconds (~${round(
          accessTokenTimeLeftSec / 60,
        )} min) left`,
      )

      /* Display modal when session is about to expire. */
      if (
        accessTokenTimeLeftSec <=
        TIME_LEFT_BEFORE_SHOWING_EXPIRATION_WARNING_SEC
      ) {
        /* If there has been user activity, then auto-refresh the token, otherwise show warning. */
        const idleTime = now() - authentication.lastActivityTime

        console.warn(`Idle for ${round(idleTime / 1000)} seconds`)
        /*
          Do not auto refresh once expiration warning is displayed.
        */

        if (
          idleTime <= IDLE_TIME_ALLOWED_BEFORE_AUTO_REFRESH &&
          !showExpirationWarning
        ) {
          refreshOktaToken()
        } else {
          /*
            There was no activity during the allowed idle time before auto-refresh happens,
            so either auto log the user out or show the expiration warning.
          */
          // eslint-disable-next-line no-lonely-if
          if (accessTokenTimeLeftSec <= TIME_LEFT_BEFORE_AUTO_LOGOUT_SEC) {
            logOut(true) /* Show log out modal after successful logout. */
          } else {
            /* Display expiration warning. */
            handleShowExpirationWarning()
          }
        }
      }
    }
  }

  const clearChatBotFromLocalStorage = () => {
    window.localStorage.setItem(localStorageKey.IS_CHAT_WINDOW_OPEN, false)
    window.localStorage.removeItem(localStorageKey.CHATBOT_STORE)
    window.localStorage.removeItem(localStorageKey.CHATBOT_ACTIVE_TABS_KEY)
  }

  function logOutHelper() {
    /*
      Remove any local storage keys related to userAccount
      on user logout.
    */
    const userAccountKeys = [
      localStorageKey.TOKEN,
      localStorageKey.USER_DETAILS,
    ]
    forEach(userAccountKeys, (key) => {
      window.localStorage?.removeItem(key)
    })

    authClient
      .signOut({ postLogoutRedirectUri: null }) // prevent redirecting on sign out until we updat and persist redux state
      .then(async () => {
        // handle properly resetting the user account state on logout
        resetUserAccount(userType.COOKIED)
        resetCartDetails()

        if (LDFFClearChatbotReduxOnLogout) {
          clearChatBotFromLocalStorage()
        }

        updateVisitorDL({
          userAccount,
          userManageAccount,
        })
        updateVisitorObject({
          userAccount,
          userManageAccount,
          LDFFReleaseSetConsistentLRSessionId,
        })

        // ensures the state is written to storage, prevents hydration issues on the next page load
        // this resolves some hydration edge cases with the user account state (logged in vs logged out)
        await persistor.flush()

        // manually navigate to the homepage after redux is updated and the persistor is flushed
        window.location.href = "/"
      })
      .catch((e) => {
        console.error(e)
      })
  }

  function logOut(showLogOutModal = false) {
    /* User is not logged in. Stop the timer because there is no need to keep checking
    if the session is active. */
    debounceSetLastActivityTime.cancel()
    stopTrackingAuthenticationStatus()
    setCloseExpirationModal(false)
    setShowExpirationWarning(false)
    setShowLogOutModal(showLogOutModal === true)

    revokeToken()
      .then(() => {
        logOutHelper()
      })
      .catch(() => {
        logOutHelper()
      })
  }

  function refreshOktaToken() {
    hideExpirationWarning()

    /* Returns a new token if the Okta session is still valid. */
    refreshToken(() => {
      // getESBTokens()
      stopTrackingAuthenticationStatus()
      startTrackingAuthenticationStatus()
    })
  }

  function handleShowExpirationWarning() {
    /* Only set to true if user didn't close out of the expiration modal to prevent the modal from
    displaying again in the checkAuthenticationStatus call after the user already closed the window. */
    if (!closeExpirationModal) {
      setShowExpirationWarning(true)
    }
  }

  function hideExpirationWarning() {
    setCloseExpirationModal(false)
    setShowExpirationWarning(false)
  }

  /* this is used to trigger adobe target, we call on page load and userState change */
  // eslint-disable-next-line no-unused-vars
  function adobeTargetCall(location) {
    const satellite = window._satellite
    const pathName = trim(location.pathname, `/`)
    const urlString = pathName.split(`/`)
    const userState = invert(userType)[userAccount.userState]
    const type =
      get(customerSelections, `customerLob`, ``) === LOB.HOME
        ? `residential`
        : `business`

    const targetData = {
      pageInfo: {
        viewName: pathName?.split("/").join("-"),
        local: `${urlString[0]}/${urlString[1]}`,
        pathName,
      },
      userInfo: {
        userState,
        userType: type,
      },
    }

    if (!isUndefined(satellite)) {
      satellite.track(`view_change`, targetData)
    }
  }

  function changeRefState() {
    setTimeout(() => {
      pushedACDL.current = false
    }, 300)
  }

  return (
    <LanguageContext.Provider
      value={{
        isLanguageInitialized,
        language: siteLanguage?.language,
      }}
    >
      <AEMPageContentContext.Provider
        value={{
          aemPageContent: aemPageData.pageData,
          getJSONStatus: aemPageData.getJSONStatus,
          siteLanguage: siteLanguage.language,
          getAEMPageJSON,
        }}
      >
        <AuthenticationContext.Provider
          value={{
            // ssr loading is too fast for userState components, it's causing issues on the pref center so adding this check to prevent render until auth client initialization is complete
            isAuthInitialized,
            userState: userAccount?.userState,
            isLoggedIn:
              userAccount?.userState === userType.LOGGED_IN ||
              !!get(userAccount, `userDetails.userId`, ``),
            userDetails: userAccount?.userDetails,
            logOut,
          }}
        >
          <div className={classes.children}>{children}</div>
          {showExpirationWarning && (
            <SessionExpirationModal
              language={siteLanguage.language}
              isVisible={showExpirationWarning}
              logOut={logOut}
              onStayLoggedInClick={() => {
                refreshOktaToken()
                setCloseExpirationModal(false)
                setShowExpirationWarning(false)
              }}
              onClose={() => {
                setCloseExpirationModal(false)
                setShowExpirationWarning(false)
              }}
            />
          )}
          {showLogOutModal && (
            <LogOutModal
              isVisible={showLogOutModal}
              onClose={() => {
                showLogOutModal(false)
              }}
            />
          )}
        </AuthenticationContext.Provider>
      </AEMPageContentContext.Provider>
    </LanguageContext.Provider>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// AppWrapper.propTypes = {
//   getToken: PropTypes.func.isRequired,
//   refreshToken: PropTypes.func.isRequired,
//   resetUserAccount: PropTypes.func.isRequired,
//   revokeToken: PropTypes.func.isRequired,
//   setLastActivityTime: PropTypes.func.isRequired,
//   setSessionIntervalId: PropTypes.func.isRequired,
//   setToken: PropTypes.func.isRequired,
//   getUserDetails: PropTypes.func.isRequired,
//   setUserDetails: PropTypes.func.isRequired,
//   setUserState: PropTypes.func.isRequired,
//   resetCartDetails: PropTypes.func.isRequired,
//   authentication: PropTypes.object.isRequired,
//   customerSelections: PropTypes.object.isRequired,
//   siteLanguage: PropTypes.object.isRequired,
//   userAccount: PropTypes.object.isRequired,
//   userManageAccount: PropTypes.object.isRequired,
//   children: PropTypes.node.isRequired,
//   model: PropTypes.object.isRequired,
//   switchLanguage: PropTypes.func.isRequired,
//   getGlobalAlerts: PropTypes.func.isRequired,
//   getLinkedAccounts: PropTypes.func.isRequired,
//   getAllCustomer: PropTypes.func.isRequired,
//   getUserPaymentDetails: PropTypes.func.isRequired,
//   getUserAccountBalance: PropTypes.func.isRequired,
//   isBrokerAccount: PropTypes.bool.isRequired,
//   myPaymentCheckout: PropTypes.object.isRequired,
//   setSelectedCurrency: PropTypes.func.isRequired,
//   aemPageData: PropTypes.object.isRequired,
//   getAEMPageJSON: PropTypes.func.isRequired,
// }

export default AppWrapper
